/**
 * Anonymous visitor time tracker.
 * ---------------------------------
 * - Mints a stable `visitor_id` in localStorage.
 * - Sends `POST /api/analytics/heartbeat` every 15 s while the user is active.
 * - Pauses when the tab is hidden or after >2 min of inactivity.
 * - Exposes `identifyVisitor({ email, pro_user_id })` to link an anonymous
 *   session to a prospect once they save a Studio draft.
 * - Exposes `setWizardStep(step)` for pages that want to report their current
 *   wizard step alongside the heartbeat (Studio only).
 */
import { API } from "../apiBase";

const VISITOR_KEY = "mc_visitor_id";
const HEARTBEAT_INTERVAL_MS = 15000;
const INACTIVITY_MS = 2 * 60 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];

let currentPage = null;
let currentStep = null;
let lastActivity = Date.now();
let lastBeatTs = Date.now();
let heartbeatTimer = null;
let bound = false;

export function getVisitorId() {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = "v_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return "v_anon";
  }
}

export function setWizardStep(step) {
  currentStep = (typeof step === "number" || (typeof step === "string" && step)) ? step : null;
}

function bumpActivity() {
  lastActivity = Date.now();
}

async function sendBeat() {
  const now = Date.now();
  const idleFor = now - lastActivity;
  if (document.hidden || idleFor > INACTIVITY_MS || !currentPage) {
    lastBeatTs = now;
    return;
  }
  const deltaSec = Math.min(30, Math.max(1, Math.round((now - lastBeatTs) / 1000)));
  lastBeatTs = now;
  try {
    await fetch(`${API}/analytics/heartbeat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        visitor_id: getVisitorId(),
        page: currentPage,
        wizard_step: currentStep,
        delta_seconds: deltaSec,
      }),
      keepalive: true,
    });
  } catch {
    /* silent — analytics must never break the app */
  }
}

export function reportPage(pathname) {
  if (currentPage !== pathname) {
    currentPage = pathname;
    lastBeatTs = Date.now(); // avoid crediting the previous page
  }
  if (!bound) {
    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, bumpActivity, { passive: true })
    );
    bound = true;
  }
  if (!heartbeatTimer) {
    heartbeatTimer = setInterval(sendBeat, HEARTBEAT_INTERVAL_MS);
    // First beat quickly so short visits still count
    setTimeout(sendBeat, 3000);
  }
}

export async function identifyVisitor({ email, pro_user_id } = {}) {
  if (!email && !pro_user_id) return;
  try {
    await fetch(`${API}/analytics/identify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        visitor_id: getVisitorId(),
        email: email || null,
        pro_user_id: pro_user_id || null,
      }),
      keepalive: true,
    });
  } catch {
    /* silent */
  }
}
